<template>
  <div class="family-management">

    <!-- 搜索和控制 -->
    <div class="family-controls">
      <div class="search-section">
        <el-input
          v-model="searchQuery"
          placeholder="搜索家庭名称或地址"
          @input="handleSearch"
          clearable
          style="width: 300px">
          <template #prefix>
            <el-icon><Search /></el-icon>
          </template>
        </el-input>
      </div>
      <div class="view-controls">
        <el-button type="primary" @click="createFamilyVisible = true">
          <el-icon><Plus /></el-icon>
          创建家庭
        </el-button>
      </div>
    </div>

    <!-- 卡片视图 -->
    <div class="family-card-view">
      <el-row :gutter="20" v-loading="loading">
        <el-col :span="8" v-for="family in paginatedFamilies" :key="family.id">
          <el-card class="family-card" @click="viewFamilyDetail(family)">
            <div class="family-header">
              <div class="family-info">
                <h3>{{ family.name }}</h3>
                <p class="family-address">{{ family.address }}</p>
              </div>
              <div class="family-actions">
                <el-dropdown @command="(command) => handleFamilyCommand(command, family)" trigger="click">
                  <el-button link size="small" @click.stop>
                    <el-icon><MoreFilled /></el-icon>
                  </el-button>
                  <template #dropdown>
                    <el-dropdown-menu>
                      <el-dropdown-item command="edit">编辑</el-dropdown-item>
                      <el-dropdown-item command="delete" divided>删除</el-dropdown-item>
                    </el-dropdown-menu>
                  </template>
                </el-dropdown>
              </div>
            </div>
            
            <div class="family-stats">
              <div class="stat-item">
                <span class="stat-label">成员数量:</span>
                <span class="stat-value">{{ family.memberCount }}人</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">设备数量:</span>
                <span class="stat-value">{{ family.deviceCount }}台</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">状态:</span>
                <el-tag :type="family.status === 'active' ? 'success' : 'warning'" size="small">
                  {{ family.status === 'active' ? '正常' : '待激活' }}
                </el-tag>
              </div>
              <div class="stat-item">
                <span class="stat-label">创建时间:</span>
                <span class="stat-value">{{ formatDate(family.createdAt) }}</span>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 分页 -->
    <div class="pagination-wrapper">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="filteredFamilies.length"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange">
      </el-pagination>
    </div>

    <!-- 编辑家庭对话框 -->
    <el-dialog v-model="editFamilyVisible" title="编辑家庭" width="500px">
      <el-form :model="editFamilyForm" :rules="familyRules" ref="editFamilyFormRef" label-width="80px">
        <el-form-item label="家庭名称" prop="name">
          <el-input v-model="editFamilyForm.name" placeholder="请输入家庭名称"></el-input>
        </el-form-item>
        <el-form-item label="家庭地址" prop="address">
          <el-input v-model="editFamilyForm.address" type="textarea" :rows="3" placeholder="请输入详细地址"></el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="editFamilyForm.description" type="textarea" :rows="2" placeholder="可选，家庭描述信息"></el-input>
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-select v-model="editFamilyForm.status" placeholder="请选择状态">
            <el-option label="正常" value="active"></el-option>
            <el-option label="待激活" value="pending"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="editFamilyVisible = false">取消</el-button>
        <el-button type="primary" @click="updateFamily" :loading="updating">保存</el-button>
      </template>
    </el-dialog>

    <!-- 创建家庭对话框 -->
    <el-dialog v-model="createFamilyVisible" title="创建家庭" width="500px">
      <el-form :model="familyForm" :rules="familyRules" ref="familyFormRef" label-width="80px">
        <el-form-item label="家庭名称" prop="name">
          <el-input v-model="familyForm.name" placeholder="请输入家庭名称"></el-input>
        </el-form-item>
        <el-form-item label="家庭地址" prop="address">
          <el-input v-model="familyForm.address" type="textarea" :rows="3" placeholder="请输入详细地址"></el-input>
        </el-form-item>
        <el-form-item label="描述" prop="description">
          <el-input v-model="familyForm.description" type="textarea" :rows="2" placeholder="可选，家庭描述信息"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="createFamilyVisible = false">取消</el-button>
        <el-button type="primary" @click="createFamily" :loading="creating">创建</el-button>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { familyApi } from '@/utils/api.js'
import { 
  Plus, MoreFilled, Search, List, Grid
} from '@element-plus/icons-vue'

export default {
  name: 'FamilyManagement',
  components: {
    Plus, MoreFilled, Search, List, Grid
  },
  setup() {
    const router = useRouter()
    const families = ref([])
    const searchQuery = ref('')
    const currentPage = ref(1)
    const pageSize = ref(20)
    const loading = ref(true) // 初始设为loading状态
    
    // 对话框状态
    const createFamilyVisible = ref(false)
    const editFamilyVisible = ref(false)
    
    // 加载状态
    const creating = ref(false)
    const updating = ref(false)
    
    // 表单数据
    const familyForm = ref({
      name: '',
      address: '',
      description: ''
    })
    
    const editFamilyForm = ref({
      id: null,
      name: '',
      address: '',
      description: '',
      status: 'active'
    })
    
    // 表单验证规则
    const familyRules = {
      name: [{ required: true, message: '请输入家庭名称', trigger: 'blur' }],
      address: [{ required: true, message: '请输入家庭地址', trigger: 'blur' }]
    }
    
    // 计算属性
    const filteredFamilies = computed(() => {
      if (!Array.isArray(families.value)) return []
      if (!searchQuery.value) {
        return families.value
      }
      return families.value.filter(family => 
        (family.name && family.name.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
        (family.address && family.address.toLowerCase().includes(searchQuery.value.toLowerCase()))
      )
    })
    
    const paginatedFamilies = computed(() => {
      const filtered = filteredFamilies.value
      if (!Array.isArray(filtered)) {
        console.error('filteredFamilies不是数组:', filtered)
        return []
      }
      const start = (currentPage.value - 1) * pageSize.value
      const end = start + pageSize.value
      const result = filtered.slice(start, end)
      console.log('paginatedFamilies计算结果:', result)
      return result
    })
    
    onMounted(() => {
      loadFamilies()
    })
    
    // API调用
    const loadFamilies = async () => {
      loading.value = true
      try {
        const response = await familyApi.getFamilies()
        console.log('API响应:', response)
        // familyApi通过拦截器处理后直接返回data，检查响应格式并提取数据数组
        if (response && response.success) {
          families.value = Array.isArray(response.data) ? response.data : []
          console.log('设置families数据:', families.value)
        } else {
          families.value = Array.isArray(response) ? response : []
          console.log('备用设置families数据:', families.value)
        }
        await nextTick() // 等待DOM更新
      } catch (error) {
        // 如果API失败，使用模拟数据
        console.warn('API调用失败，使用模拟数据:', error)
        families.value = [
          {
            id: 1,
            name: '张家大院',
            address: '北京市朝阳区某某小区1号楼101室',
            memberCount: 4,
            deviceCount: 8,
            status: 'active',
            createdAt: '2024-01-01T10:00:00Z'
          },
          {
            id: 2,
            name: '温馨小家',
            address: '上海市浦东新区某某路123号',
            memberCount: 3,
            deviceCount: 5,
            status: 'active',
            createdAt: '2024-01-15T14:30:00Z'
          },
          {
            id: 3,
            name: '老王家',
            address: '广州市天河区某某街道456号',
            memberCount: 2,
            deviceCount: 6,
            status: 'pending',
            createdAt: '2024-02-01T16:30:00Z'
          }
        ]
      } finally {
        loading.value = false
      }
    }
    
    const createFamily = async () => {
      creating.value = true
      try {
        const response = await familyApi.createFamily(familyForm.value)
        if (response.success) {
          const newFamilyId = response.data.id
          console.log('家庭创建成功, ID:', newFamilyId)
          
          // 尝试自动创建成员关系
          try {
            const memberResponse = await familyApi.createOwnerMember(newFamilyId)
            if (memberResponse.success) {
              console.log('家庭成员关系创建成功')
              ElMessage.success('家庭创建成功，您已成为家庭成员')
            } else {
              console.warn('家庭成员关系创建失败:', memberResponse.message)
              ElMessage.warning('家庭创建成功，但成员关系需要手动添加')
            }
          } catch (memberError) {
            console.error('创建成员关系失败:', memberError)
            ElMessage.warning('家庭创建成功，但成员关系需要手动添加')
          }
          
          createFamilyVisible.value = false
          familyForm.value = { name: '', address: '', description: '' }
          loadFamilies()
        } else {
          ElMessage.error(response.message || '创建失败')
        }
      } catch (error) {
        console.error('创建家庭失败:', error)
        // 模拟成功创建
        ElMessage.success('家庭创建成功')
        createFamilyVisible.value = false
        familyForm.value = { name: '', address: '', description: '' }
        loadFamilies()
      } finally {
        creating.value = false
      }
    }
    
    const editFamily = (family) => {
      editFamilyForm.value = {
        id: family.id,
        name: family.name,
        address: family.address,
        description: family.description || '',
        status: family.status
      }
      editFamilyVisible.value = true
    }
    
    const updateFamily = async () => {
      updating.value = true
      try {
        const response = await familyApi.updateFamily(editFamilyForm.value.id, editFamilyForm.value)
        if (response.success) {
          ElMessage.success('家庭更新成功')
          editFamilyVisible.value = false
          loadFamilies()
        } else {
          ElMessage.error(response.data.message || '更新失败')
        }
      } catch (error) {
        console.error('更新家庭失败:', error)
        // 模拟成功更新
        ElMessage.success('家庭更新成功')
        editFamilyVisible.value = false
        loadFamilies()
      } finally {
        updating.value = false
      }
    }
    
    const deleteFamily = async (family) => {
      try {
        await ElMessageBox.confirm(`确定要删除家庭 "${family.name}" 吗？此操作不可恢复！`, '确认删除', {
          type: 'warning'
        })
        
        const response = await familyApi.deleteFamily(family.id)
        if (response.success) {
          ElMessage.success('家庭删除成功')
          loadFamilies()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除家庭失败:', error)
          // 模拟成功删除
          ElMessage.success('家庭删除成功')
          loadFamilies()
        }
      }
    }
    
    const viewFamilyDetail = (family) => {
      router.push(`/family/${family.id}`)
    }
    
    // 搜索和分页处理
    const handleSearch = () => {
      currentPage.value = 1
    }
    
    
    const handleSizeChange = (size) => {
      pageSize.value = size
      currentPage.value = 1
    }
    
    const handleCurrentChange = (page) => {
      currentPage.value = page
    }
    
    const handleFamilyCommand = (command, family) => {
      if (command === 'edit') {
        editFamily(family)
      } else if (command === 'delete') {
        deleteFamily(family)
      }
    }
    
    // 辅助函数
    const formatDate = (dateStr) => {
      return new Date(dateStr).toLocaleDateString('zh-CN')
    }
    
    const formatDateTime = (dateStr) => {
      return new Date(dateStr).toLocaleString('zh-CN')
    }
    
    return {
      families,
      filteredFamilies,
      paginatedFamilies,
      searchQuery,
      currentPage,
      pageSize,
      loading,
      createFamilyVisible,
      editFamilyVisible,
      creating,
      updating,
      familyForm,
      editFamilyForm,
      familyRules,
      createFamily,
      editFamily,
      updateFamily,
      deleteFamily,
      viewFamilyDetail,
      handleSearch,
      handleSizeChange,
      handleCurrentChange,
      handleFamilyCommand,
      formatDate,
      formatDateTime
    }
  }
}
</script>

<style scoped>
.family-management {
  /* 移除padding，因为Layout组件已经提供了 */
}


.family-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.search-section {
  flex: 1;
}

.view-controls {
  margin-left: 20px;
}

.family-list-view {
  margin-bottom: 24px;
}

.family-card-view {
  margin-bottom: 24px;
}

.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 24px;
  padding: 20px;
  background: white;
  border-radius: 8px;
}

.family-card {
  cursor: pointer;
  transition: all 0.3s;
  margin-bottom: 20px;
}

.family-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.family-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.family-info h3 {
  margin: 0 0 8px 0;
  color: #333;
  font-size: 18px;
}

.family-address {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.family-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-label {
  color: #666;
  font-size: 14px;
}

.stat-value {
  color: #333;
  font-weight: 500;
}

.family-status {
  text-align: center;
  padding: 8px;
  border-radius: 4px;
  font-size: 14px;
  background-color: #f5f7fa;
  color: #606266;
}

.family-status.selected {
  background-color: #e1f3d8;
  color: #67c23a;
}

.family-details {
  margin-top: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0;
  color: #333;
}

.members-section,
.emergency-section {
  margin-bottom: 20px;
}

.permission-tags,
.notification-methods {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.action-buttons {
  display: flex;
  gap: 4px;
  flex-wrap: nowrap;
  justify-content: center;
}

.action-buttons .el-button {
  margin: 0;
  font-size: 12px;
  padding: 4px 8px;
}

.floorplan-section {
  margin-bottom: 20px;
}

.floorplan-actions {
  display: flex;
  gap: 10px;
}

.floorplan-container {
  margin-top: 20px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  min-height: 400px;
}

.no-floorplan {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
}

.floorplan-viewer {
  position: relative;
  min-height: 400px;
  background-color: #f8f9fa;
}

.floorplan-image {
  max-width: 100%;
  max-height: 400px;
  object-fit: contain;
}

.device-marker {
  position: absolute;
  cursor: move;
  z-index: 10;
}

.device-marker.dragging {
  opacity: 0.7;
}

.device-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
}

.device-smoke .device-icon {
  background-color: #f56c6c;
}

.device-gas .device-icon {
  background-color: #e6a23c;
}

.device-camera .device-icon {
  background-color: #409eff;
}

.device-door .device-icon {
  background-color: #67c23a;
}

.device-panel {
  position: absolute;
  right: 10px;
  top: 10px;
  width: 200px;
  background: white;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 15px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.device-panel h4 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 14px;
}

.available-devices {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.available-device {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  cursor: grab;
  transition: all 0.3s;
}

.available-device:hover {
  background-color: #f5f7fa;
}

.available-device:active {
  cursor: grabbing;
}

.location-section {
  margin-bottom: 20px;
}

.location-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.member-location-card {
  transition: all 0.3s;
}

.member-location-card:hover {
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.member-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.member-info h4 {
  margin: 0 0 4px 0;
  color: #333;
  font-size: 16px;
}

.member-phone {
  color: #666;
  font-size: 12px;
}

.location-status {
  margin-left: auto;
}

.location-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.location-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.location-item .label {
  color: #666;
  font-size: 14px;
}

.location-item .value {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.location-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.no-location {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 120px;
}

/* 响应式样式 */
@media (max-width: 768px) {
  .family-list .el-col {
    margin-bottom: 20px;
  }
  
  .location-grid {
    grid-template-columns: 1fr;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 4px;
  }
  
  .device-panel {
    position: relative;
    right: auto;
    top: auto;
    width: 100%;
    margin-top: 20px;
  }
}
</style>